import type {
  ModelDocument,
  Operation,
  Diagnostic,
  LossReport,
  Diagram,
} from "../shared/contracts.js";
import { createModel, uid, clone, DomainError, syncDocument } from "./model.js";
/** View names share a case-insensitive namespace within the model. */
export function validateDiagramName(document: ModelDocument, name: string, exceptId?: string): string {
  const trimmed = name.trim();
  if (!trimmed) throw new DomainError("NAME_REQUIRED", "Give the subject area or view a name.");
  if (trimmed.length > 512) throw new DomainError("INVALID_NAME", "Use a name of at most 512 characters.");
  const key = trimmed.normalize("NFKC").toLocaleLowerCase("en-US");
  if (document.diagrams.some((d) => d.diagramId !== exceptId && d.name.trim().normalize("NFKC").toLocaleLowerCase("en-US") === key))
    throw new DomainError("DIAGRAM_NAME_EXISTS", "A diagram with this name already exists in the model.");
  return trimmed;
}
export function nextViewName(document: ModelDocument): string {
  let number = document.diagrams.reduce((max, d) => {
    const match = /^view\s+(\d+)$/i.exec(d.name.trim());
    const value = match ? Number(match[1]) : 0;
    return Number.isSafeInteger(value) ? Math.max(max, value) : max;
  }, 0) + 1;
  if (!Number.isSafeInteger(number)) number = 1;
  const names = new Set(document.diagrams.map((d) => d.name.trim().toLocaleLowerCase("en-US")));
  while (names.has(`view ${number}`)) number++;
  return `View ${number}`;
}
function sourceDiagram(document: ModelDocument, id?: string): Diagram {
  if (id && !document.diagrams.some((d) => d.diagramId === id)) throw new DomainError("NOT_FOUND", "Source diagram does not exist.");
  const diagram = document.diagrams.find((d) => d.diagramId === id) ?? document.diagrams.find((d) => d.representationId === document.model.activeRepresentation && d.role === "main") ?? document.diagrams.find((d) => d.representationId === document.model.activeRepresentation);
  if (!diagram) throw new DomainError("NOT_FOUND", "Choose a source diagram.");
  if (document.representations.find((r) => r.representationId === diagram.representationId)?.baseline)
    throw new DomainError("BASELINE_IMMUTABLE", "Preserved baselines are read-only. Select a working representation.");
  return diagram;
}
function selectedIds(document: ModelDocument, diagram: Diagram, objectIds: string[]): Set<string> {
  const selected = new Set(objectIds);
  for (const id of selected) {
    const obj = document.objects.find((o) => o.objectId === id);
    if (!obj || obj.representationId !== diagram.representationId || obj.ownerObjectId)
      throw new DomainError("INVALID_SELECTION", "Selections must be top-level objects in the source representation.");
  }
  return selected;
}
function copyAppearances(document: ModelDocument, source: Diagram, selected: Set<string>) {
  return [...selected].map((id, index) => {
    const existing = source.appearances.find((a) => a.objectId === id) ?? document.diagrams.find((d) => d.role === "main" && d.representationId === source.representationId)?.appearances.find((a) => a.objectId === id);
    return existing ? { ...clone(existing), appearanceId: uid() } : { appearanceId: uid(), objectId: id, position: { x: (index % 4) * 400, y: Math.floor(index / 4) * 360 } };
  });
}
export interface ViewCreationOptions { parentDiagramId?: string; includeInParent?: boolean }
/** A subject area groups shared semantic objects with its own saved geometry. */
export function createSubjectArea(document: ModelDocument, name: string, objectIds: string[], sourceDiagramId?: string): Operation[] {
  return createScopedDiagram(document, "subject-area", name, objectIds, sourceDiagramId);
}
/** Empty views are useful temporary workspaces; a default unique name is supplied. */
export function createView(document: ModelDocument, name: string | undefined, objectIds: string[], sourceDiagramId?: string, options: ViewCreationOptions = {}): Operation[] {
  return createScopedDiagram(document, "view", name ?? nextViewName(document), objectIds, sourceDiagramId, options);
}
function createScopedDiagram(document: ModelDocument, role: "subject-area" | "view", name: string, objectIds: string[], sourceDiagramId?: string, options: ViewCreationOptions = {}, independentExtraction = false): Operation[] {
  const source = sourceDiagram(document, sourceDiagramId), selected = selectedIds(document, source, objectIds);
  const operations: Operation[] = [];
  if (options.parentDiagramId) {
    const parent = document.diagrams.find((d) => d.diagramId === options.parentDiagramId);
    if (role !== "view" || !parent || parent.role !== "subject-area" || parent.representationId !== source.representationId)
      throw new DomainError("INVALID_VIEW_PARENT", "Choose a subject area in the same representation.");
    const missing = [...selected].filter((id) => !parent.appearances.some((a) => a.objectId === id));
    if (missing.length && !options.includeInParent) throw new DomainError("PARENT_MEMBERSHIP_REQUIRED", "Confirm adding these entities to the parent subject area as well.");
    if (missing.length) operations.push(...addEntitiesToDiagram(document, parent.diagramId, missing));
  }
  const diagram: Diagram = {
    diagramId: uid(), diagramVersion: 1, name: independentExtraction ? name.trim() : validateDiagramName(document, name), role,
    ...(options.parentDiagramId ? { parentDiagramId: options.parentDiagramId } : {}),
    perspective: source.perspective, representationId: source.representationId, notation: source.notation,
    appearance: clone(source.appearance), appearances: copyAppearances(document, source, selected),
    connectors: source.connectors.filter((c) => {
      const r = document.relationships.find((r) => r.relationshipId === c.relationshipId);
      return r && selected.has(r.sourceObjectId) && selected.has(r.targetObjectId);
    }).map(clone),
  };
  operations.push({ type: "diagram.create", diagram });
  return operations;
}
export function addEntitiesToDiagram(document: ModelDocument, diagramId: string, objectIds: string[], options: { includeInParent?: boolean } = {}): Operation[] {
  const target = sourceDiagram(document, diagramId);
  if (target.diagramId !== diagramId) throw new DomainError("NOT_FOUND", "Diagram does not exist.");
  const selected = selectedIds(document, target, objectIds), operations: Operation[] = [];
  if (target.parentDiagramId) {
    const parent = document.diagrams.find((d) => d.diagramId === target.parentDiagramId)!;
    const missing = [...selected].filter((id) => !parent.appearances.some((a) => a.objectId === id));
    if (missing.length && !options.includeInParent) throw new DomainError("PARENT_MEMBERSHIP_REQUIRED", "Confirm adding these entities to the parent subject area as well.");
    if (missing.length) operations.push(...addEntitiesToDiagram(document, parent.diagramId, missing));
  }
  const missing = new Set([...selected].filter((id) => !target.appearances.some((a) => a.objectId === id)));
  for (const appearance of copyAppearances(document, target, missing)) operations.push({ type: "appearance.create", diagramId, appearance });
  return operations;
}
export function removeEntitiesFromDiagram(document: ModelDocument, diagramId: string, objectIds: string[]): Operation[] {
  const target = sourceDiagram(document, diagramId);
  if (target.role === "main") throw new DomainError("MAIN_DIAGRAM_COMPLETE", "Main diagrams show every entity. Remove entities from a view instead.");
  const selected = new Set(objectIds);
  if (document.diagrams.some((d) => d.parentDiagramId === diagramId && d.appearances.some((a) => selected.has(a.objectId))))
    throw new DomainError("CHILD_VIEW_MEMBERSHIP", "Remove these entities from the nested views first.");
  const relationships = new Set(document.relationships.filter((r) => selected.has(r.sourceObjectId) || selected.has(r.targetObjectId)).map((r) => r.relationshipId));
  return [
    ...target.connectors.filter((c) => relationships.has(c.relationshipId)).map((c) => ({ type: "connector.remove", diagramId, relationshipId: c.relationshipId })),
    ...target.appearances.filter((a) => selected.has(a.objectId)).map((a) => ({ type: "appearance.delete", diagramId, appearanceId: a.appearanceId })),
  ];
}
export function promoteViewToSubjectArea(document: ModelDocument, diagramId: string, name?: string): Operation[] {
  const target = sourceDiagram(document, diagramId);
  if (target.diagramId !== diagramId || target.role !== "view") throw new DomainError("INVALID_VIEW", "Select a view to promote.");
  return [{ type: "diagram.update", diagramId, changes: { role: "subject-area", name: validateDiagramName(document, name ?? target.name, diagramId) }, unset: ["parentDiagramId"] }];
}
/** Intentional independent extraction with explicit provenance; sources are never removed. */
export function extractModel(
  document: ModelDocument,
  name: string,
  objectIds: string[],
  sourceDiagramId?: string,
): {
  document: ModelDocument;
  diagnostics: Diagnostic[];
  lossReport: LossReport;
} {
  const diagram = createScopedDiagram(
      document,
      "view",
      name,
      objectIds,
      sourceDiagramId,
      {},
      true,
    )[0].diagram!,
    sourceRep = document.representations.find(
      (r) => r.representationId === diagram.representationId,
    )!;
  const model = createModel(
      name,
      sourceRep.kind === "conceptual"
        ? "conceptual"
        : sourceRep.kind === "physical"
          ? "physical"
          : "logical",
      document.model.family,
      sourceRep.targetProfile ?? document.model.targetProfile,
    ),
    repId = model.model.activeRepresentation;
  const included = new Set(objectIds);
  let expanded = true;
  while (expanded) {
    expanded = false;
    for (const obj of document.objects)
      if (
        obj.ownerObjectId &&
        included.has(obj.ownerObjectId) &&
        !included.has(obj.objectId)
      ) {
        included.add(obj.objectId);
        expanded = true;
      }
  }
  const ids = new Map([...included].map((id) => [id, uid()])),
    diagnostics: Diagnostic[] = [],
    lossReport: LossReport = {
      preserved: [
        "Selected objects, definitions, fields and ordered identifiers",
        "Internal relationships and diagram positions",
      ],
      translated: [
        "Independent stable identities with explicit provenance mappings",
      ],
      preservedOpaque: [],
      lost: [],
      unresolved: [],
    };
  for (const source of document.objects.filter((o) =>
    included.has(o.objectId),
  )) {
    const obj = clone(source);
    obj.objectId = ids.get(source.objectId)!;
    obj.representationId = repId;
    if (obj.ownerObjectId) obj.ownerObjectId = ids.get(obj.ownerObjectId);
    if (obj.members) obj.members = obj.members.map((id) => ids.get(id)!);
    if (obj.grain) obj.grain = obj.grain.map((id) => ids.get(id) ?? id);
    obj.extensions = {
      ...obj.extensions,
      extraction: {
        sourceModelId: document.model.modelId,
        sourceRepresentationId: sourceRep.representationId,
        sourceObjectId: source.objectId,
        origin: "explicit-copy",
      },
    };
    model.objects.push(obj);
    model.mappings.push({
      mappingId: uid(),
      sourceRefs: [
        {
          modelId: document.model.modelId,
          representationId: sourceRep.representationId,
          objectId: source.objectId,
        },
      ],
      destinationRefs: [
        {
          modelId: model.model.modelId,
          representationId: repId,
          objectId: obj.objectId,
        },
      ],
      mappingKind: "derived",
      origin: "declared",
      reviewState: "accepted",
      rationale:
        "Explicit independent model extraction; changes do not synchronize automatically.",
    });
  }
  const relationshipIds = new Map<string, string>();
  for (const rel of document.relationships.filter(
    (r) => r.representationId === sourceRep.representationId,
  )) {
    if (included.has(rel.sourceObjectId) && included.has(rel.targetObjectId)) {
      const relationshipId = uid();
      relationshipIds.set(rel.relationshipId, relationshipId);
      model.relationships.push({
        ...clone(rel),
        relationshipId,
        representationId: repId,
        sourceObjectId: ids.get(rel.sourceObjectId)!,
        targetObjectId: ids.get(rel.targetObjectId)!,
        sourceAttributeIds: rel.sourceAttributeIds?.map((id) => ids.get(id)!),
        targetAttributeIds: rel.targetAttributeIds?.map((id) => ids.get(id)!),
      });
    } else if (
      included.has(rel.sourceObjectId) ||
      included.has(rel.targetObjectId)
    ) {
      lossReport.unresolved.push(`External relationship ${rel.name}`);
      diagnostics.push({
        code: "EXTRACTION_EXTERNAL_RELATIONSHIP",
        severity: "warning",
        message: `'${rel.name}' crosses the selection boundary. Its external entity has not been silently copied.`,
        objectId: rel.relationshipId,
      });
      model.dependencies.push({
        instanceId: uid(),
        kind: "unresolved-extraction-reference",
        sourceModelId: document.model.modelId,
        sourceRelationshipId: rel.relationshipId,
        sourceObjectId: rel.sourceObjectId,
        targetObjectId: rel.targetObjectId,
      });
    }
  }
  model.diagrams[0] = {
    ...diagram,
    role: "main",
    diagramId: model.diagrams[0].diagramId,
    representationId: repId,
    appearances: diagram.appearances.map((a) => ({
      ...a,
      objectId: ids.get(a.objectId)!,
    })),
    connectors: diagram.connectors
      .filter((c) => relationshipIds.has(c.relationshipId))
      .map((c) => ({
        ...c,
        relationshipId: relationshipIds.get(c.relationshipId)!,
      })),
  };
  syncDocument(model);
  return { document: model, diagnostics, lossReport };
}
